import { db } from "../../firebase/firebase";
import { collection, getDocs, doc } from "firebase/firestore";

// ------------------------------------------------------------
// 1) Load plan history for all drivers
// ------------------------------------------------------------
export async function loadDriverPlanMap() {
  const snap = await getDocs(collection(db, "driverPlanHistory"));
  const map = {};

  snap.forEach((docSnap) => {
    const d = docSnap.data();
    if (!d.driverId) return;

    if (!map[d.driverId]) {
      map[d.driverId] = [];
    }

    map[d.driverId].push({
      driverId: d.driverId,
      driverName: d.driverName || "",
      planType: d.planType || "FIXED",
      startDate: d.startDate?.toDate
        ? d.startDate.toDate()
        : new Date(d.startDate),
      endDate: d.endDate
        ? (d.endDate.toDate ? d.endDate.toDate() : new Date(d.endDate))
        : null,

      // ⭐ MUST HAVE for updating plan history
      _docRef: doc(db, "driverPlanHistory", docSnap.id),
    });
  });

  // Sort ASC
  Object.values(map).forEach((list) => {
    list.sort((a, b) => a.startDate - b.startDate);
  });

  return map;
}

// ------------------------------------------------------------
// 2) Get plan for specific date
// ------------------------------------------------------------
export function getDriverPlanForDate({ driverId, date, planMap }) {
  const list = planMap[driverId];
  if (!list || list.length === 0) return "FIXED";

  const reference = new Date(date);
  if (Number.isNaN(reference.getTime())) return "FIXED";

  // First use the exact timestamp. This is essential when a driver changes
  // both car and plan on the same calendar date. Example:
  //   old car / SALARY ends at 01:00
  //   new car / FIXED starts at 02:00
  // The old car sheet is resolved from its own assignment timestamp and the
  // new car sheet is resolved from its own assignment timestamp.
  const exactMatches = list.filter((p) => {
    const start = new Date(p.startDate);
    const end = p.endDate ? new Date(p.endDate) : new Date(8640000000000000);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }

    return reference >= start && reference <= end;
  });

  if (exactMatches.length > 0) {
    exactMatches.sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
    return exactMatches[0].planType || "FIXED";
  }

  // Compatibility fallback for older callers that supply only a calendar
  // date (midnight) rather than an assignment timestamp. Preserve the prior
  // same-day rule: if multiple plan records touch that date, the plan with
  // the latest start date wins.
  const dayStart = new Date(reference);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(reference);
  dayEnd.setHours(23, 59, 59, 999);

  const sameDayMatches = list.filter((p) => {
    const start = new Date(p.startDate);
    const end = p.endDate ? new Date(p.endDate) : new Date(8640000000000000);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }

    return start <= dayEnd && end >= dayStart;
  });

  if (sameDayMatches.length === 0) return "FIXED";

  sameDayMatches.sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  return sameDayMatches[0].planType || "FIXED";
}
